import { useMemo, useState } from "react";
import { ModalShell } from "../modals/ModalShell";
import { formatYmdLong, localTodayYmd } from "../../lib/dates";
import {
  defaultJourneyWindow,
  genericPhaseSegments,
  journeyWindowChoices,
  runwayWeeks,
  windowStartYmd,
  type RacePlanImpact
} from "../../lib/journeyWindow";
import "./planner.css";

/**
 * The J4 "open time → goal" mini confirm sheet (rules doc: "one confirm sheet:
 * start, cadence — 10 seconds, prefilled"). Planner add-event contexts land
 * here after picking a race in open time; onboarding uses the inline window
 * row instead. Reuses the wizard's phasebar visual grammar (D2) with generic
 * build/peak/taper shading — no library fetch needed.
 */

/** Generic phase bar for an N-week window (`.fgp-wz-phasebar` styles). */
export function WindowPhasebar({ weeks }: { weeks: number }) {
  const segs = genericPhaseSegments(weeks);
  const total = segs.reduce((s, x) => s + x.weeks, 0) || 1;
  return (
    <div className="fgp-wz-phasebar" aria-hidden>
      {segs.map((s) => (
        <span
          key={s.label}
          style={{ width: `${(s.weeks / total) * 100}%`, background: s.color }}
        />
      ))}
    </div>
  );
}

export type RacePlanWindowChoice = {
  startYmd: string;
  weeks: number;
  dropWeeks: number;
};

/**
 * 5b — the plain-language impact of creating this plan, computed from real
 * journey data by `computeRacePlanImpact` (never hardcoded). Rendered as a terse
 * caption block so the additive apply reads as honest, not destructive: existing
 * sessions stay put, a rolling journey merely resumes after the race. Absent →
 * the sheet renders no impact block (nothing meaningful to say). Structurally the
 * `RacePlanImpact` the funnel computes — aliased so the sheet's prop reads local.
 */
export type RacePlanImpactSummary = RacePlanImpact;

export function RacePlanConfirmModal({
  eventName,
  raceYmd,
  libraryLengths,
  impact,
  onConfirm,
  onSkip,
  onBuildPlan,
  onClose
}: {
  eventName: string;
  raceYmd: string;
  /** Library plan lengths for the distance when the caller has them (D2). */
  libraryLengths?: number[] | null;
  /** 5b — real-data impact summary (see {@link RacePlanImpactSummary}). */
  impact?: RacePlanImpactSummary | null;
  /** Create the race plan with the confirmed window + cadence. */
  onConfirm: (choice: RacePlanWindowChoice) => void;
  /** Save the event only (no journey). */
  onSkip: () => void;
  /**
   * Opt-in to the heavy wizard (C2): when provided, render a third, tertiary
   * "Build me a training plan →" action. The funnel only wires this on the goal
   * path (never a tune-up), so its absence keeps the plain two-button layout.
   */
  onBuildPlan?: () => void;
  onClose: () => void;
}) {
  const today = localTodayYmd();
  const choices = useMemo(
    () => journeyWindowChoices(libraryLengths, raceYmd, today),
    [libraryLengths, raceYmd, today]
  );
  const [weeks, setWeeks] = useState(
    () => defaultJourneyWindow({ raceYmd, todayYmd: today, libraryLengths }).weeks
  );
  const [dropWeeks, setDropWeeks] = useState(2);
  const startTodayWeeks = runwayWeeks(raceYmd, today);
  const startYmd =
    weeks >= startTodayWeeks ? today : windowStartYmd(raceYmd, weeks);

  return (
    <ModalShell
      eyebrow="Race plan"
      title={`Fuel your ${eventName} training?`}
      subtitle="We build fuel boxes across this window, ending on race day — add your own workouts and we fuel them."
      onClose={onClose}
      width={600}
      footerSpread
      footer={
        onBuildPlan ? (
          // Three actions crowd the spread on mobile, so the opt-in "Build me a
          // training plan" reads as a tertiary/link-style action on the left
          // (`fg-btn--ghost`), while the two decisive actions stay grouped on the
          // right. On mobile the `foot--mobile` rules stack all three full-width.
          <>
            <button
              type="button"
              className="fg-btn fg-btn--ghost fg-btn--sm"
              onClick={onBuildPlan}>
              Build me a training plan →
            </button>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="fg-btn fg-btn--soft fg-btn--sm" onClick={onSkip}>
                Just save the event
              </button>
              <button
                type="button"
                className="fg-btn fg-btn--primary fg-btn--sm"
                onClick={() => onConfirm({ startYmd, weeks, dropWeeks })}>
                Create race plan →
              </button>
            </div>
          </>
        ) : (
          <>
            <button type="button" className="fg-btn fg-btn--ghost fg-btn--sm" onClick={onSkip}>
              Just save the event
            </button>
            <button
              type="button"
              className="fg-btn fg-btn--primary fg-btn--sm"
              onClick={() => onConfirm({ startYmd, weeks, dropWeeks })}>
              Create race plan →
            </button>
          </>
        )
      }>
      <div className="fgp-modalshell__pad">
        <div className="fgp-modal-field-label">Training window</div>
        <div className="fgp-window-chiprow" role="radiogroup" aria-label="Training window length">
          {choices.map((w) => {
            const on = weeks === w;
            return (
              <button
                key={w}
                type="button"
                role="radio"
                aria-checked={on}
                className={`fg-chip${on ? " is-on" : ""}`}
                onClick={() => setWeeks(w)}>
                {w >= startTodayWeeks ? `Start today · ${w} wk` : `${w} weeks`}
              </button>
            );
          })}
        </div>
        <WindowPhasebar weeks={weeks} />
        <p className="fgp-window-caption">
          {formatYmdLong(startYmd)} → race day · {weeks} {weeks === 1 ? "week" : "weeks"}
        </p>

        <div className="fgp-modal-field-label" style={{ marginTop: 14 }}>
          New box every…
        </div>
        <div className="fgp-window-chiprow" role="radiogroup" aria-label="New box every…">
          {[1, 2, 3, 4].map((n) => {
            const on = dropWeeks === n;
            return (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={on}
                className={`fg-chip${on ? " is-on" : ""}`}
                onClick={() => setDropWeeks(n)}>
                {n === 1 ? "Week" : `${n} weeks`}
              </button>
            );
          })}
        </div>
        <p className="fgp-window-caption">
          We prepare each box when you&rsquo;re ready for it — you review and buy them one at a
          time. Nothing upfront. Change any of this later in plan settings.
        </p>

        {/* 5b — real-data impact: reassure that the additive apply moves nothing.
            Only the lines with something true to say render; an all-empty impact
            (a first race on a clean calendar) shows no block. Reuse
            PasteConflictModal's collision vocabulary if that language is ever
            needed here — this build path only ADDS, so it stays reassurance. */}
        {impact &&
          (() => {
            const lines: string[] = [];
            if (impact.existingWorkoutsInWindow > 0) {
              lines.push(
                `Your ${impact.existingWorkoutsInWindow} existing ${
                  impact.existingWorkoutsInWindow === 1 ? "workout stays" : "workouts stay"
                } exactly where ${impact.existingWorkoutsInWindow === 1 ? "it is" : "they are"}.`
              );
            }
            if (impact.boxesRegroup) {
              lines.push(`Your fuel boxes re-group around ${eventName} race day.`);
            }
            if (impact.rollingResumesAfterYmd) {
              lines.push(
                `Your flexible training picks up again after ${formatYmdLong(
                  impact.rollingResumesAfterYmd
                )}.`
              );
            }
            if (lines.length === 0) return null;
            return (
              <div className="fgp-impact">
                <p className="fgp-impact__head">
                  Adding {eventName} · {formatYmdLong(raceYmd)}
                </p>
                <ul className="fgp-impact__list">
                  {lines.map((l) => (
                    <li key={l} className="fgp-impact__item">
                      {l}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })()}
      </div>
    </ModalShell>
  );
}
